import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlDpDashboard {
  total: number;
  active: number;
  on_leave: number;
  terminated: number;
  in_experience: number;
}

export interface ControlDpRole {
  id: number;
  tenant_id: number;
  name: string;
  base_salary?: number | null;
}

export interface ControlDpEmployee {
  id: number;
  tenant_id: number;
  user_id?: number | null;
  status: string;
  full_name: string;
  employee_code?: string | null;
  cpf?: string | null;
  rg?: string | null;
  birth_date?: string | null;
  birth_location?: string | null;
  race?: string | null;
  age?: number | null;
  gender?: string | null;
  marital_status?: string | null;
  mother_name?: string | null;
  father_name?: string | null;
  education_level?: string | null;
  personal_email?: string | null;
  corporate_email?: string | null;
  phone?: string | null;
  cep?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  hired_at?: string | null;
  experience_45_days?: string | null;
  experience_90_days?: string | null;
  esocial_code?: string | null;
  ctps_number?: string | null;
  pis?: string | null;
  pcd?: number | null;
  cbo_code?: string | null;
  bank?: string | null;
  bank_branch?: string | null;
  bank_account?: string | null;
  bank_account_type?: string | null;
  contract_type?: string | null;
  salary?: number | null;
  role_id?: number | null;
  role_name?: string | null;
  base_salary?: number | null;
  sector_id?: number | null;
  sector_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  location_id?: number | null;
  location_name?: string | null;
  photo_url?: string | null;
  admission_expires_at?: string | null;
  admission_token?: string | null;
  admission_link?: string | null;
  admission_email_sent_at?: string | null;
  admission_alert_count?: number | null;
  admission_has_alert?: number | null;
  created_at?: string | null;
}

export interface ControlDpOccurrence {
  id: number;
  tenant_id: number;
  employee_id: number;
  type: string;
  start_date?: string | null;
  end_date?: string | null;
  description?: string | null;
  notes?: string | null;
  certificate_institution?: string | null;
  certificate_course?: string | null;
  certificate_modality?: string | null;
  certificate_knowledge_area?: string | null;
  created_at?: string | null;
}

export interface ControlDpHistory {
  id: number;
  tenant_id: number;
  employee_id: number;
  user_id?: number | null;
  action_type: string;
  description?: string | null;
  attachment_name?: string | null;
  attachment_path?: string | null;
  created_at?: string | null;
}

export interface ControlDpAdmissionAlertRoute {
  id: string;
  alert_rule_id?: number | null;
  alert_name?: string | null;
  validation_unit?: string | null;
  travel_mode?: string | null;
  condition_operator?: string | null;
  condition_value_min?: number | null;
  condition_value_max?: number | null;
  measured_value?: number | null;
  measured_unit?: string | null;
  origin_address?: string | null;
  destination_address?: string | null;
  created_at?: string | null;
  map_path?: string | null;
}

export interface ControlDpAttachment {
  id: number;
  tenant_id: number;
  employee_id: number;
  file_path: string;
  file_name: string;
  file_type?: string | null;
  created_at?: string | null;
}

export interface ControlDpOccurrenceAttachment {
  id: number;
  tenant_id: number;
  employee_id: number;
  occurrence_id: number;
  file_path: string;
  file_name: string;
  file_type?: string | null;
  created_at?: string | null;
}

export interface ControlDpImport {
  id: number;
  tenant_id: number;
  import_type: string;
  file_name?: string | null;
  total_rows: number;
  status: string;
  processed_count?: number | null;
  error_count?: number | null;
  pending_count?: number | null;
  created_at?: string | null;
}

export interface ControlDpDigitalAdmission {
  id: number;
  tenant_id: number;
  cpf?: string | null;
  full_name: string;
  birth_date?: string | null;
  hired_at?: string | null;
  personal_email?: string | null;
  status: string;
  completed_at?: string | null;
  created_at?: string | null;
}

export interface ControlDpDigitalAdmissionVerification {
  header: {
    tenant_id: number;
    full_name: string;
    cpf: string;
    personal_email: string;
    accessed_at: string | null;
    expires_at: string | null;
    hired_at: string | null;
    status: string;
  };
  fields: {
    rg?: string | null;
    birth_date?: string | null;
    birth_location?: string | null;
    race?: string | null;
    gender?: string | null;
    marital_status?: string | null;
    mother_name?: string | null;
    father_name?: string | null;
    education_level?: string | null;
    phone?: string | null;
    emergency_contact_name?: string | null;
    emergency_contact_phone?: string | null;
    cep?: string | null;
    street?: string | null;
    number?: string | null;
    complement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    photo_url?: string | null;
    notes?: string | null;
  };
  employee_id: number;
}

export interface ControlDpEmployeeDocumentFile {
  id: number;
  folder_id: number;
  name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  uploaded_at: string;
}

export interface ControlDpEmployeeDocumentsResponse {
  employee_id: number;
  folders: {
    root_folder_id: number;
    RG_CPF: { id: number; name: string };
    ADDRESS: { id: number; name: string };
    BANK: { id: number; name: string };
    OTHERS: { id: number; name: string };
  };
  files: {
    RG_CPF: ControlDpEmployeeDocumentFile[];
    ADDRESS: ControlDpEmployeeDocumentFile[];
    BANK: ControlDpEmployeeDocumentFile[];
    OTHERS: ControlDpEmployeeDocumentFile[];
  };
}

export interface ControlDpDigitalAdmissionSubmitResponse {
  success: boolean;
  status: string;
  alert_count?: number;
  alert_rules?: string[];
}

export interface ControlDpAlertRule {
  id: number;
  tenant_id: number;
  alert_name: string;
  location_type: 'CEP' | 'ADDRESS';
  location_value: string;
  validation_unit: 'RADIUS_KM' | 'TRAVEL_TIME';
  travel_mode: 'DRIVING' | 'WALKING' | 'TRANSIT' | null;
  condition_operator: 'GREATER_THAN' | 'LESS_THAN' | 'BETWEEN' | 'GREATER_OR_EQUAL' | 'LESS_OR_EQUAL';
  condition_value_min: number;
  condition_value_max: number | null;
  action_type: 'GENERATE_ALERT';
  active: number;
  created_by?: number | null;
  created_by_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ControlDpFeedbackTemplate {
  id: number;
  tenant_id: number;
  survey_id: number;
  title: string;
  description?: string | null;
  sector_id?: number | null;
  sector_name?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  sector_ids_json?: string | number[] | null;
  department_ids_json?: string | number[] | null;
  sector_names_label?: string | null;
  department_names_label?: string | null;
  status?: 'ACTIVE' | 'INACTIVE';
  created_by?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ControlDpEmployeeFeedback {
  id: number;
  tenant_id: number;
  employee_id: number;
  feedback_template_id: number;
  survey_id: number;
  controltask_task_id?: number | null;
  status: 'DRAFT' | 'SCHEDULED' | 'APPLIED';
  scheduled_for?: string | null;
  applied_at?: string | null;
  notes?: string | null;
  collaborator_notes?: string | null;
  answers_json?: string | null;
  template_title?: string | null;
  template_description?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ControlDpFeedbackDashboardSummary {
  total: number;
  draft: number;
  scheduled: number;
  applied: number;
  unique_employees: number;
  applied_rate: number;
  last_applied_at?: string | null;
}

export interface ControlDpFeedbackDashboardTemplateRow {
  id: number;
  title: string;
  total: number;
  draft: number;
  scheduled: number;
  applied: number;
  applied_rate: number;
  last_applied_at?: string | null;
}

export interface ControlDpFeedbackDashboardMonthlyRow {
  month_key: string;
  label: string;
  total: number;
  draft: number;
  scheduled: number;
  applied: number;
}

export interface ControlDpFeedbackDashboardDepartmentRow {
  label: string;
  count: number;
}

export interface ControlDpFeedbackDashboardRecentAppliedRow {
  id: number;
  employee_id: number;
  employee_name: string;
  template_title: string;
  applied_at?: string | null;
}

export interface ControlDpFeedbackQuestionInsightOption {
  key: string;
  label: string;
  count: number;
  percent: number;
}

export interface ControlDpFeedbackQuestionInsight {
  question_id: number;
  question_type: string;
  title: string;
  required: boolean;
  total_answers: number;
  options: ControlDpFeedbackQuestionInsightOption[];
  stats?: {
    min?: number | null;
    max?: number | null;
    average?: number | null;
  } | null;
}

export interface ControlDpFeedbackDashboard {
  summary: ControlDpFeedbackDashboardSummary;
  templates: ControlDpFeedbackDashboardTemplateRow[];
  monthly: ControlDpFeedbackDashboardMonthlyRow[];
  departments: ControlDpFeedbackDashboardDepartmentRow[];
  recent_applied: ControlDpFeedbackDashboardRecentAppliedRow[];
  insights_template_id?: number | null;
  insights_template_title?: string | null;
  question_insights: ControlDpFeedbackQuestionInsight[];
  question_insights_message?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ControlDpService {
  private readonly baseUrl = `${API_BASE_URL}/controldp`;

  constructor(private http: HttpClient) {}

  getDashboard(tenantId: number): Observable<ControlDpDashboard> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpDashboard>(`${this.baseUrl}/dashboard`, { params });
  }

  getFeedbackDashboard(tenantId: number, templateId?: number | null, months = 12): Observable<ControlDpFeedbackDashboard> {
    let params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('months', String(months));
    if (templateId) {
      params = params.set('template_id', String(templateId));
    }
    return this.http.get<ControlDpFeedbackDashboard>(`${this.baseUrl}/dashboard/feedback-kpis`, { params });
  }

  listRoles(tenantId: number): Observable<ControlDpRole[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpRole[]>(`${this.baseUrl}/positions`, { params });
  }

  createRole(payload: { tenant_id: number; name: string; base_salary?: number | null; created_by?: number | null }) {
    return this.http.post<ControlDpRole>(`${this.baseUrl}/positions`, payload);
  }

  updateRole(roleId: number, payload: { tenant_id: number; name: string; base_salary?: number | null }) {
    return this.http.put<ControlDpRole>(`${this.baseUrl}/positions/${roleId}`, payload);
  }

  listEmployees(tenantId: number): Observable<ControlDpEmployee[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpEmployee[]>(`${this.baseUrl}/employees`, { params });
  }

  findEmployeeByCode(tenantId: number, employeeCode: string): Observable<ControlDpEmployee> {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('employee_code', employeeCode);
    return this.http.get<ControlDpEmployee>(`${this.baseUrl}/employees/lookup`, { params });
  }

  createEmployee(payload: any) {
    return this.http.post<ControlDpEmployee>(`${this.baseUrl}/employees`, payload);
  }

  updateEmployee(id: number, payload: any) {
    return this.http.put<ControlDpEmployee>(`${this.baseUrl}/employees/${id}`, payload);
  }

  reactivateAdmissionLink(employeeId: number, payload: { tenant_id: number; user_id?: number | null }) {
    return this.http.post<{ success: true; employee: ControlDpEmployee }>(
      `${this.baseUrl}/employees/${employeeId}/admission-link/reactivate`,
      payload
    );
  }

  listOccurrences(tenantId: number, employeeId?: number) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (employeeId) {
      params = params.set('employee_id', employeeId);
    }
    return this.http.get<ControlDpOccurrence[]>(`${this.baseUrl}/occurrences`, { params });
  }

  createOccurrence(payload: any) {
    return this.http.post<ControlDpOccurrence>(`${this.baseUrl}/occurrences`, payload);
  }

  listOccurrenceAttachments(tenantId: number, occurrenceId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpOccurrenceAttachment[]>(`${this.baseUrl}/occurrences/${occurrenceId}/attachments`, { params });
  }

  uploadOccurrenceAttachment(tenantId: number, occurrenceId: number, createdBy: number, file: File) {
    const formData = new FormData();
    formData.append('tenant_id', String(tenantId));
    formData.append('created_by', String(createdBy));
    formData.append('file', file);
    return this.http.post<ControlDpOccurrenceAttachment>(`${this.baseUrl}/occurrences/${occurrenceId}/attachments`, formData);
  }

  listHistory(tenantId: number, employeeId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpHistory[]>(`${this.baseUrl}/employees/${employeeId}/history`, { params });
  }

  listEmployeeAdmissionAlertRoutes(tenantId: number, employeeId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpAdmissionAlertRoute[]>(`${this.baseUrl}/employees/${employeeId}/admission-alert-routes`, { params });
  }

  listAttachments(tenantId: number, employeeId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpAttachment[]>(`${this.baseUrl}/employees/${employeeId}/attachments`, { params });
  }

  uploadAttachment(tenantId: number, employeeId: number, createdBy: number, file: File) {
    const formData = new FormData();
    formData.append('tenant_id', String(tenantId));
    formData.append('created_by', String(createdBy));
    formData.append('file', file);
    return this.http.post<ControlDpAttachment>(`${this.baseUrl}/employees/${employeeId}/attachments`, formData);
  }

  listImports(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpImport[]>(`${this.baseUrl}/imports`, { params });
  }

  listDigitalAdmissions(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpDigitalAdmission[]>(`${this.baseUrl}/digital-admissions/pending`, { params });
  }

  createDigitalAdmission(payload: Partial<ControlDpDigitalAdmission>) {
    return this.http.post<ControlDpDigitalAdmission>(`${this.baseUrl}/digital-admissions`, payload);
  }

  listAlertRules(tenantId: number, activeOnly = false) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (activeOnly) {
      params = params.set('active_only', '1');
    }
    return this.http.get<ControlDpAlertRule[]>(`${this.baseUrl}/alerts`, { params });
  }

  createAlertRule(payload: {
    tenant_id: number;
    user_id: number | null;
    alert_name: string;
    location_type: 'CEP' | 'ADDRESS';
    location_value: string;
    validation_unit: 'RADIUS_KM' | 'TRAVEL_TIME';
    travel_mode?: 'DRIVING' | 'WALKING' | 'TRANSIT' | null;
    condition_operator: 'GREATER_THAN' | 'LESS_THAN' | 'BETWEEN' | 'GREATER_OR_EQUAL' | 'LESS_OR_EQUAL';
    condition_value_min: number;
    condition_value_max?: number | null;
    action_type?: 'GENERATE_ALERT';
    active?: 0 | 1;
  }) {
    return this.http.post<ControlDpAlertRule>(`${this.baseUrl}/alerts`, payload);
  }

  updateAlertRule(
    alertRuleId: number,
    payload: {
      tenant_id: number;
      user_id: number | null;
      alert_name: string;
      location_type: 'CEP' | 'ADDRESS';
      location_value: string;
      validation_unit: 'RADIUS_KM' | 'TRAVEL_TIME';
      travel_mode?: 'DRIVING' | 'WALKING' | 'TRANSIT' | null;
      condition_operator: 'GREATER_THAN' | 'LESS_THAN' | 'BETWEEN' | 'GREATER_OR_EQUAL' | 'LESS_OR_EQUAL';
      condition_value_min: number;
      condition_value_max?: number | null;
      action_type?: 'GENERATE_ALERT';
      active?: 0 | 1;
    }
  ) {
    return this.http.put<ControlDpAlertRule>(`${this.baseUrl}/alerts/${alertRuleId}`, payload);
  }

  verifyDigitalAdmission(token: string, pin: string) {
    return this.http.post<ControlDpDigitalAdmissionVerification>(`${this.baseUrl}/digital-admissions/verify`, { token, pin });
  }

  getDigitalAdmissionDocuments(token: string, pin: string) {
    const params = new HttpParams().set('pin', pin);
    return this.http.get<ControlDpEmployeeDocumentsResponse>(`${this.baseUrl}/digital-admissions/${token}/documents`, { params });
  }

  uploadDigitalAdmissionDocument(token: string, pin: string, category: 'RG_CPF' | 'ADDRESS' | 'BANK' | 'OTHERS', file: File) {
    const formData = new FormData();
    formData.append('pin', pin);
    formData.append('category', category);
    formData.append('file', file);
    return this.http.post<{ category: string; file: ControlDpEmployeeDocumentFile }>(
      `${this.baseUrl}/digital-admissions/${token}/documents/upload`,
      formData
    );
  }

  deleteDigitalAdmissionDocument(token: string, pin: string, fileId: number) {
    const params = new HttpParams().set('pin', pin);
    return this.http.delete<{ success: boolean }>(`${this.baseUrl}/digital-admissions/${token}/documents/${fileId}`, { params });
  }

  submitDigitalAdmission(token: string, pin: string) {
    return this.http.post<ControlDpDigitalAdmissionSubmitResponse>(`${this.baseUrl}/digital-admissions/${token}/submit`, { pin });
  }

  getEmployeeDocuments(tenantId: number, employeeId: number, userId?: number) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http.get<ControlDpEmployeeDocumentsResponse>(`${this.baseUrl}/employees/${employeeId}/documents`, { params });
  }

  uploadEmployeeDocument(
    tenantId: number,
    employeeId: number,
    userId: number,
    category: 'RG_CPF' | 'ADDRESS' | 'BANK' | 'OTHERS',
    file: File
  ) {
    const formData = new FormData();
    formData.append('tenant_id', String(tenantId));
    formData.append('user_id', String(userId));
    formData.append('category', category);
    formData.append('file', file);
    return this.http.post<{ category: string; file: ControlDpEmployeeDocumentFile }>(
      `${this.baseUrl}/employees/${employeeId}/documents/upload`,
      formData
    );
  }

  deleteEmployeeDocument(tenantId: number, employeeId: number, fileId: number, userId?: number) {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http.delete<{ success: boolean }>(`${this.baseUrl}/employees/${employeeId}/documents/${fileId}`, { params });
  }

  listImportFailedRows(tenantId: number, importId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<Array<Record<string, any>>>(`${this.baseUrl}/imports/${importId}/failed`, { params });
  }

  listSectorsForExport(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<Array<{ id: number; name: string }>>(`${this.baseUrl}/exports/sectors`, { params });
  }

  listDepartmentsForExport(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<Array<{ id: number; name: string; sector_id: number }>>(`${this.baseUrl}/exports/departments`, { params });
  }

  listRolesForExport(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<Array<{ id: number; name: string; tenant_id: number }>>(`${this.baseUrl}/exports/roles`, { params });
  }

  createImport(payload: {
    tenant_id: number;
    created_by?: number | null;
    import_type: string;
    file_name?: string | null;
    rows: any[];
  }) {
    return this.http.post(`${this.baseUrl}/imports`, payload);
  }

  createFeedbackTemplate(payload: {
    tenant_id: number;
    survey_id: number;
    title: string;
    description?: string | null;
    sector_ids: number[];
    department_ids: number[];
    created_by?: number | null;
    updated_by?: number | null;
  }) {
    return this.http.post<ControlDpFeedbackTemplate>(`${this.baseUrl}/feedbacks`, payload);
  }

  listFeedbackTemplates(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpFeedbackTemplate[]>(`${this.baseUrl}/feedbacks`, { params });
  }

  listEmployeeFeedbacks(tenantId: number, employeeId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlDpEmployeeFeedback[]>(`${this.baseUrl}/employees/${employeeId}/feedbacks`, { params });
  }

  saveEmployeeFeedback(
    employeeId: number,
    payload: {
      tenant_id: number;
      feedback_template_id: number;
      status: 'DRAFT' | 'SCHEDULED' | 'APPLIED';
      scheduled_for?: string | null;
      notes?: string | null;
      collaborator_notes?: string | null;
      answers_json?: string | null;
      created_by?: number | null;
      updated_by?: number | null;
      feedback_id?: number | null;
    }
  ) {
    return this.http.post<ControlDpEmployeeFeedback>(`${this.baseUrl}/employees/${employeeId}/feedbacks`, payload);
  }
}
